import { useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { createSupplierPortalClient } from "@/modules/8d/api/supplier-portal";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";
import { Send, Check, Building2, Calendar, AlertCircle, Shield, Users } from "lucide-react";
import { TeamMemberForm } from "@/modules/8d/components/TeamMemberForm";
import { ActionManager } from "@/modules/8d/components/ActionManager";
import { RootCauseAnalysis } from "@/modules/8d/components/RootCauseAnalysis";
import { PreventRecurrence } from "@/modules/8d/components/PreventRecurrence";
import { TeamRecognition } from "@/modules/8d/components/TeamRecognition";
import { FileAttachments } from "@/modules/8d/components/FileAttachments";
import { format } from "date-fns";
import { useTheme } from "next-themes";
import fpIconColor from "@/assets/fp-icon-color.png";
import fpIconWhite from "@/assets/fp-icon-white.png";
import fpBannerColor from "@/assets/fp-banner-color.png";
import fpBannerWhite from "@/assets/fp-banner-white.png";

const statusColors: Record<string, string> = {
  open: "bg-warning/20 text-warning border-warning/30",
  in_progress: "bg-chart-2/20 text-chart-2 border-chart-2/30",
  pending_supplier: "bg-chart-3/20 text-chart-3 border-chart-3/30",
  pending_approval: "bg-chart-4/20 text-chart-4 border-chart-4/30",
  closed: "bg-success/20 text-success border-success/30",
};

const stepStatusColors: Record<string, string> = {
  pending: "bg-muted/20 text-muted-foreground border-border",
  in_progress: "bg-chart-2/20 text-chart-2 border-chart-2/30",
  completed: "bg-success/20 text-success border-success/30",
};

export default function SupplierClaimView() {
  const { resolvedTheme } = useTheme();
  const { claimId: claimIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [stepContent, setStepContent] = useState("");
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const supplierClient = token ? createSupplierPortalClient(token) : null;

  const logoSrc = resolvedTheme === "dark" ? fpIconWhite : fpBannerColor;

  // Verify magic token
  const { data: claim, isLoading } = useQuery({
    queryKey: ["mod_8d_supplier_claim", token],
    queryFn: async () => {
      if (!token || !supplierClient) return null;
      const data = await supplierClient.fetchClaim();
      if (!data) {
        setIsValid(false);
        return null;
      }
      if (claimIdParam && data.id !== claimIdParam) {
        setIsValid(false);
        return null;
      }
      setIsValid(true);
      return data;
    },
    enabled: !!token,
  });

  const { data: steps } = useQuery({
    queryKey: ["mod_8d_steps", claim?.id],
    queryFn: () => supplierClient!.fetchSteps(),
    enabled: isValid === true && !!claim?.id,
  });

  const { data: comments } = useQuery({
    queryKey: ["mod_8d_comments", claim?.id],
    queryFn: () => supplierClient!.fetchComments(),
    enabled: isValid === true && !!claim?.id,
  });

  const updateStepMutation = useMutation({
    mutationFn: async ({ stepNumber, content, status, stepTitle }: { stepNumber: number; content: string; status: string; stepTitle: string }) => {
      if (!claim?.id) throw new Error("Claim not found");
      if (!supplierClient) throw new Error("Missing supplier access");

      await supplierClient.updateStepByNumber(stepNumber, content, status);

      // Fetch originator info and send notification
      if (claim?.originator?.email) {
        await supabase.functions.invoke("send-step-notification", {
          body: {
            claimId: claim.id,
            claimNumber: claim.claim_number,
            claimTitle: claim.title,
            stepNumber,
            stepTitle,
            stepStatus: status,
            supplierName: claim.supplier?.name || "Supplier",
            originatorEmail: claim.originator.email,
            originatorName: claim.originator.full_name,
            factoryName: claim.factory?.name || "Factory",
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_steps", claim?.id] });
      setEditingStep(null);
      toast({ title: "Step updated" });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async () => {
      if (!claim?.id || !claim?.tenant_id) throw new Error("Claim not found");
      if (!supplierClient) throw new Error("Missing supplier access");
      await supplierClient.createComment(newComment, claim.supplier?.name || "Supplier");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_comments", claim?.id] });
      setNewComment("");
      toast({ title: "Comment added" });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (isValid === false || !claim) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center py-12">
            <AlertCircle className="h-16 w-16 text-destructive mb-4" />
            <h1 className="text-xl font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground text-center">
              This link is invalid or has expired. Please contact the factory for a new access link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt="Factory Pilot" className="h-8" />
            <span className="font-semibold">Factory Pilot</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            Supplier Portal
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="space-y-6">
          {/* Claim Header */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-muted-foreground">
                {claim.claim_number}
              </span>
              <Badge variant="outline" className={statusColors[claim.status] || ""}>
                {claim.status.replace("_", " ")}
              </Badge>
            </div>
            <h1 className="text-2xl font-bold">{claim.title}</h1>
            <p className="text-muted-foreground mt-1">
              Opened by {claim.factory?.name}
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              {/* 8D Steps */}
              <Card>
                <CardHeader>
                  <CardTitle>8D Report Steps</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Please complete each step of the 8D process
                  </p>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="space-y-2">
                    {steps?.map((step) => (
                      <AccordionItem key={step.step_number} value={`step-${step.step_number}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className={stepStatusColors[step.status] || ""}>
                              {step.status === "completed" ? (
                                <Check className="h-3 w-3 mr-1" />
                              ) : null}
                              D{step.step_number}
                            </Badge>
                            <span className="font-medium">{step.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 pb-6">
                          {/* Special handling for D1 - Team Formation */}
                          {step.step_number === 1 ? (
                            <div className="space-y-6">
                              <TeamMemberForm claimId={claim!.id} supplierToken={token ?? undefined} />
                              
                              <div className="border-t border-border pt-4">
                                <p className="text-sm text-muted-foreground mb-3">Additional notes (optional):</p>
                                {editingStep === step.step_number ? (
                                  <div className="space-y-4">
                                    <Textarea
                                      value={stepContent}
                                      onChange={(e) => setStepContent(e.target.value)}
                                      placeholder="Add any additional notes about team formation..."
                                      rows={3}
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() =>
                                          updateStepMutation.mutate({
                                            stepNumber: step.step_number,
                                            content: stepContent,
                                            status: "in_progress",
                                            stepTitle: step.title,
                                          })
                                        }
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() =>
                                          updateStepMutation.mutate({
                                            stepNumber: step.step_number,
                                            content: stepContent,
                                            status: "completed",
                                            stepTitle: step.title,
                                          })
                                        }
                                      >
                                        <Check className="h-4 w-4 mr-1" />
                                        Mark Complete
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setEditingStep(null)}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-4">
                                    {step.content && (
                                      <p className="text-sm whitespace-pre-wrap">{step.content}</p>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingStep(step.step_number);
                                        setStepContent(step.content || "");
                                      }}
                                    >
                                      {step.content ? "Edit Notes" : "Add Notes"}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* D3 - Containment Actions */}
                              {step.step_number === 3 && (
                                <div className="mb-4">
                                    <ActionManager 
                                      claimId={claim!.id} 
                                      stepNumber={3} 
                                      title="Containment Actions"
                                      supplierToken={token ?? undefined}
                                      claimForNotification={claim}
                                    />
                                  {step.content && <div className="border-t border-border mt-4 pt-4" />}
                                </div>
                              )}

                              {/* D4 - Root Cause Analysis */}
                              {step.step_number === 4 && (
                                <div className="mb-4">
                                  <RootCauseAnalysis claimId={claim!.id} supplierToken={token ?? undefined} />
                                  {step.content && <div className="border-t border-border mt-4 pt-4" />}
                                </div>
                              )}

                              {/* D5 - Permanent Corrective Actions */}
                              {step.step_number === 5 && (
                                <div className="mb-4">
                                    <ActionManager 
                                      claimId={claim!.id} 
                                      stepNumber={5} 
                                      title="Permanent Corrective Actions"
                                      supplierToken={token ?? undefined}
                                      claimForNotification={claim}
                                    />
                                  {step.content && <div className="border-t border-border mt-4 pt-4" />}
                                </div>
                              )}

                              {/* D6 - Implement & Validate */}
                              {step.step_number === 6 && (
                                <div className="mb-4">
                                    <ActionManager 
                                      claimId={claim!.id} 
                                      stepNumber={6} 
                                      title="Implementation & Validation Tasks"
                                      supplierToken={token ?? undefined}
                                      claimForNotification={claim}
                                    />
                                  {step.content && <div className="border-t border-border mt-4 pt-4" />}
                                </div>
                              )}

                              {/* D7 - Prevent Recurrence */}
                              {step.step_number === 7 && (
                                <div className="mb-4">
                                  <PreventRecurrence claimId={claim!.id} supplierToken={token ?? undefined} />
                                  {step.content && <div className="border-t border-border mt-4 pt-4" />}
                                </div>
                              )}

                              {/* D8 - Congratulate Team */}
                              {step.step_number === 8 && (
                                <div className="mb-4">
                                  <TeamRecognition claimId={claim!.id} isSupplierView={true} supplierToken={token ?? undefined} />
                                  {step.content && <div className="border-t border-border mt-4 pt-4" />}
                                </div>
                              )}

                              {/* Standard text editor for all steps */}
                              {editingStep === step.step_number ? (
                                <div className="space-y-4">
                                  <Textarea
                                    value={stepContent}
                                    onChange={(e) => setStepContent(e.target.value)}
                                    placeholder="Enter your response..."
                                    rows={4}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        updateStepMutation.mutate({
                                          stepNumber: step.step_number,
                                          content: stepContent,
                                          status: "in_progress",
                                          stepTitle: step.title,
                                        })
                                      }
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() =>
                                        updateStepMutation.mutate({
                                          stepNumber: step.step_number,
                                          content: stepContent,
                                          status: "completed",
                                          stepTitle: step.title,
                                        })
                                      }
                                    >
                                      <Check className="h-4 w-4 mr-1" />
                                      Mark Complete
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingStep(null)}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {step.content ? (
                                    <p className="text-sm whitespace-pre-wrap">{step.content}</p>
                                  ) : (
                                    <p className="text-sm text-muted-foreground italic">
                                      No additional notes yet - click Edit to add your input
                                    </p>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingStep(step.step_number);
                                      setStepContent(step.content || "");
                                    }}
                                  >
                                    {step.content ? "Edit Notes" : "Add Notes"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              {/* Comments */}
              <Card>
                <CardHeader>
                  <CardTitle>Communication</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4 max-h-[400px] overflow-y-auto">
                    {comments?.map((comment) => (
                      <div
                        key={comment.id}
                        className={`p-4 rounded-lg ${
                          comment.author_type === "supplier"
                            ? "bg-primary/10 border border-primary/20"
                            : "bg-secondary/50 border border-border"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                          <span className="font-medium">
                            {comment.author_type === "factory" ? "Factory" : "You"}
                          </span>
                          <span>•</span>
                          <span>{format(new Date(comment.created_at), "MMM d, yyyy HH:mm")}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    ))}
                    {!comments?.length && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No messages yet
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Send a message to the factory..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && newComment.trim()) {
                          e.preventDefault();
                          addCommentMutation.mutate();
                        }
                      }}
                    />
                    <Button
                      size="icon"
                      disabled={!newComment.trim() || addCommentMutation.isPending}
                      onClick={() => addCommentMutation.mutate()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Claim Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{claim.factory?.name}</p>
                      <p className="text-muted-foreground">Customer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Opened {format(new Date(claim.created_at), "MMM d, yyyy")}</span>
                  </div>
                </CardContent>
              </Card>

              <FileAttachments claimId={claim!.id} authorType="supplier" supplierToken={token ?? undefined} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
